import type { PageType } from "../../types";
import type { ProfileType } from "../../lib/client-guide-content";
import { isInternalProfile } from "../../lib/client-guide-content";

/**
 * Visites guidées interactives multi-écran (lot 1).
 *
 * Chaque visite est une suite d'étapes déclaratives. Le moteur
 * (`InteractiveGuide.tsx`) navigue réellement vers `page`, exécute `reveal`
 * (clic d'un élément, ex. un onglet) puis surligne `anchor` (sélecteur
 * `data-tour`). Une étape sans `anchor` = popover centré.
 *
 * Le contenu (titre + corps riche) vit en i18n sous `guide.*` (FR + EN).
 * `guideSectionId` relie la visite à la section du guide écrit (`/help`).
 */

export interface GuideStep {
  /** Écran requis pour cette étape (navigation auto si nécessaire). */
  page?: PageType;
  /** Sélecteur à cliquer avant de surligner (ex. un onglet Radix). */
  reveal?: string;
  /** Sélecteur `data-tour` à surligner. Absent = popover centré. */
  anchor?: string;
  titleKey: string;
  bodyKey: string;
  side?: "top" | "bottom" | "left" | "right";
  /**
   * Mode « observation » : c'est l'UTILISATEUR qui agit (ex. remplir l'assistant).
   * On masque « Suivant », la cible reste interactive, et la visite avance
   * automatiquement quand `advanceWhen` apparaît ou `advanceWhenGone` disparaît.
   * Si la cible (`anchor`) disparaît sans condition remplie → la visite s'arrête.
   */
  observe?: boolean;
  /** Sélecteur dont l'APPARITION fait avancer la visite (mode observation). */
  advanceWhen?: string;
  /** Sélecteur dont la DISPARITION fait avancer la visite (mode observation). */
  advanceWhenGone?: string;
}

export interface Walkthrough {
  id: string;
  /** Profils clients autorisés (internes KESTIO voient tout). */
  allowedProfiles: ProfileType[];
  titleKey: string;
  /** Nom d'icône lucide pour le hub d'aide. */
  iconName: string;
  /** Section correspondante du guide écrit (bouton « En savoir plus »). */
  guideSectionId?: string;
  steps: GuideStep[];
}

const ALL_CLIENTS: ProfileType[] = ["ORG", "MNG", "GES", "RH", "BNF"];

export const WALKTHROUGHS: Walkthrough[] = [
  {
    id: "discover-home",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.discoverHome.title",
    iconName: "Compass",
    guideSectionId: "prise-en-main",
    steps: [
      { page: "dashboard", titleKey: "guide.walkthroughs.discoverHome.s1Title", bodyKey: "guide.walkthroughs.discoverHome.s1Body" },
      { page: "dashboard", anchor: '[data-tour="my-seances"]', side: "bottom", titleKey: "guide.walkthroughs.discoverHome.s2Title", bodyKey: "guide.walkthroughs.discoverHome.s2Body" },
      { page: "dashboard", anchor: '[data-tour="field-cards"]', side: "top", titleKey: "guide.walkthroughs.discoverHome.s3Title", bodyKey: "guide.walkthroughs.discoverHome.s3Body" },
      { page: "dashboard", anchor: '[data-tour="dashboard-simulators"]', side: "top", titleKey: "guide.walkthroughs.discoverHome.s4Title", bodyKey: "guide.walkthroughs.discoverHome.s4Body" },
      { page: "dashboard", anchor: '[data-tour="dashboard-challenges"]', side: "top", titleKey: "guide.walkthroughs.discoverHome.sChallengesTitle", bodyKey: "guide.walkthroughs.discoverHome.sChallengesBody" },
      { page: "dashboard", anchor: '[data-tour="home-evaluation"]', side: "top", titleKey: "guide.walkthroughs.discoverHome.sEvalTitle", bodyKey: "guide.walkthroughs.discoverHome.sEvalBody" },
      { page: "dashboard", anchor: '[data-tour="home-progress"]', side: "left", titleKey: "guide.walkthroughs.discoverHome.s5Title", bodyKey: "guide.walkthroughs.discoverHome.s5Body" },
      { page: "dashboard", anchor: '[data-tour="profile-menu"]', side: "bottom", titleKey: "guide.walkthroughs.discoverHome.s6Title", bodyKey: "guide.walkthroughs.discoverHome.s6Body" },
      { titleKey: "guide.walkthroughs.discoverHome.s7Title", bodyKey: "guide.walkthroughs.discoverHome.s7Body" },
    ],
  },
  {
    id: "discover-pilot",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.discoverPilot.title",
    iconName: "Compass",
    guideSectionId: "prise-en-main",
    steps: [
      { page: "encadrant", titleKey: "guide.walkthroughs.discoverPilot.s1Title", bodyKey: "guide.walkthroughs.discoverPilot.s1Body" },
      { page: "encadrant", anchor: '[data-tour="encadrant-nav"]', side: "bottom", titleKey: "guide.walkthroughs.discoverPilot.s2Title", bodyKey: "guide.walkthroughs.discoverPilot.s2Body" },
      { page: "encadrant", anchor: '[data-tour="view-switch"]', side: "bottom", titleKey: "guide.walkthroughs.discoverPilot.s3Title", bodyKey: "guide.walkthroughs.discoverPilot.s3Body" },
      { page: "encadrant", anchor: '[data-tour="profile-menu"]', side: "bottom", titleKey: "guide.walkthroughs.discoverPilot.s4Title", bodyKey: "guide.walkthroughs.discoverPilot.s4Body" },
      { titleKey: "guide.walkthroughs.discoverPilot.s5Title", bodyKey: "guide.walkthroughs.discoverPilot.s5Body" },
    ],
  },
  {
    id: "create-case",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createCase.title",
    iconName: "Target",
    guideSectionId: "creer-cas-simulation",
    // La visite s'arrête à l'ouverture de l'assistant : on ne surligne PAS le wizard
    // (ses listes déroulantes s'ouvrent dans un portail hors zone → l'overlay
    // bloquerait la sélection). L'utilisateur remplit l'assistant librement ensuite.
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-concevoir"]', anchor: '[data-tour="sub-nav-cases"]', side: "right", titleKey: "guide.walkthroughs.createCase.s1Title", bodyKey: "guide.walkthroughs.createCase.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-cases"]', anchor: '[data-tour="account-new-case"]', side: "bottom", observe: true, advanceWhen: '[data-tour="case-wizard"]', titleKey: "guide.walkthroughs.createCase.s4Title", bodyKey: "guide.walkthroughs.createCase.s4Body" },
    ],
  },
  {
    id: "create-offer",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createOffer.title",
    iconName: "Package",
    guideSectionId: "creer-offres",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-concevoir"]', anchor: '[data-tour="sub-nav-offers"]', side: "right", titleKey: "guide.walkthroughs.createOffer.s1Title", bodyKey: "guide.walkthroughs.createOffer.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-offers"]', anchor: '[data-tour="account-new-offer"]', side: "bottom", observe: true, advanceWhen: '[data-tour="offer-form"]', titleKey: "guide.walkthroughs.createOffer.s2Title", bodyKey: "guide.walkthroughs.createOffer.s2Body" },
    ],
  },
  {
    id: "create-persona",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createPersona.title",
    iconName: "Users",
    guideSectionId: "creer-personae",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-concevoir"]', anchor: '[data-tour="sub-nav-personae"]', side: "right", titleKey: "guide.walkthroughs.createPersona.s1Title", bodyKey: "guide.walkthroughs.createPersona.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-personae"]', anchor: '[data-tour="account-new-persona"]', side: "bottom", observe: true, advanceWhen: '[data-tour="persona-form"]', titleKey: "guide.walkthroughs.createPersona.s2Title", bodyKey: "guide.walkthroughs.createPersona.s2Body" },
    ],
  },
  {
    id: "create-context",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createContext.title",
    iconName: "Layers",
    guideSectionId: "configurer-contextes",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-concevoir"]', anchor: '[data-tour="sub-nav-contexts"]', side: "right", titleKey: "guide.walkthroughs.createContext.s1Title", bodyKey: "guide.walkthroughs.createContext.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-contexts"]', anchor: '[data-tour="context-new"]', side: "bottom", observe: true, advanceWhen: '[data-tour="context-form"]', titleKey: "guide.walkthroughs.createContext.s2Title", bodyKey: "guide.walkthroughs.createContext.s2Body" },
    ],
  },
  {
    id: "studio",
    allowedProfiles: ["ORG", "MNG", "GES", "RH", "BNF"],
    titleKey: "guide.walkthroughs.studio.title",
    iconName: "Dumbbell",
    guideSectionId: "studio",
    steps: [
      { page: "m-entrainer", anchor: '[data-tour="studio-kpis"]', side: "bottom", titleKey: "guide.walkthroughs.studio.s1Title", bodyKey: "guide.walkthroughs.studio.s1Body" },
      { page: "m-entrainer", anchor: '[data-tour="studio-engagements"]', side: "top", titleKey: "guide.walkthroughs.studio.s2Title", bodyKey: "guide.walkthroughs.studio.s2Body" },
      { page: "m-entrainer", anchor: '[data-tour="studio-simulators"]', side: "top", titleKey: "guide.walkthroughs.studio.s3Title", bodyKey: "guide.walkthroughs.studio.s3Body" },
      { page: "m-entrainer", anchor: '[data-tour="studio-history"]', side: "bottom", titleKey: "guide.walkthroughs.studio.s4Title", bodyKey: "guide.walkthroughs.studio.s4Body" },
    ],
  },
  {
    id: "train-simulator",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.trainSimulator.title",
    iconName: "Mic",
    guideSectionId: "s-entrainer",
    steps: [
      { page: "dashboard", anchor: '[data-tour="dashboard-simulators"]', side: "top", titleKey: "guide.walkthroughs.trainSimulator.s1Title", bodyKey: "guide.walkthroughs.trainSimulator.s1Body" },
      // L'utilisateur clique une carte → page de présentation du simulateur.
      { page: "dashboard", anchor: '[data-tour^="simulator-card-"]', side: "right", observe: true, advanceWhen: '[data-tour="agent-present-train"]', titleKey: "guide.walkthroughs.trainSimulator.s2Title", bodyKey: "guide.walkthroughs.trainSimulator.s2Body" },
      // Sur la présentation : on surligne « S'entraîner » et on explique la suite.
      { page: "agentPresentation", anchor: '[data-tour="agent-present-train"]', side: "top", titleKey: "guide.walkthroughs.trainSimulator.s3Title", bodyKey: "guide.walkthroughs.trainSimulator.s3Body" },
      { titleKey: "guide.walkthroughs.trainSimulator.s4Title", bodyKey: "guide.walkthroughs.trainSimulator.s4Body" },
    ],
  },
  {
    id: "sessions-live",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.sessionsLive.title",
    iconName: "CalendarCheck",
    guideSectionId: "simulateurs-coachs",
    steps: [
      { page: "dashboard", anchor: '[data-tour="field-cards"]', side: "bottom", titleKey: "guide.walkthroughs.sessionsLive.s1Title", bodyKey: "guide.walkthroughs.sessionsLive.s1Body" },
      // L'utilisateur clique « Préparer » → ouvre le Coach Prépa (formulaire).
      { page: "dashboard", anchor: '[data-tour="home-prep-card"]', side: "top", observe: true, advanceWhen: '[data-tour="coach-form"]', titleKey: "guide.walkthroughs.sessionsLive.s2Title", bodyKey: "guide.walkthroughs.sessionsLive.s2Body" },
      // Sur le Coach Prépa : on surligne le formulaire et on explique.
      { page: "coach", anchor: '[data-tour="coach-form"]', side: "left", titleKey: "guide.walkthroughs.sessionsLive.s3Title", bodyKey: "guide.walkthroughs.sessionsLive.s3Body" },
      { titleKey: "guide.walkthroughs.sessionsLive.s4Title", bodyKey: "guide.walkthroughs.sessionsLive.s4Body" },
    ],
  },
  {
    id: "add-criteria",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.addCriteria.title",
    iconName: "ClipboardCheck",
    guideSectionId: "ajouter-criteres",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-concevoir"]', anchor: '[data-tour="sub-nav-criteria"]', side: "right", titleKey: "guide.walkthroughs.addCriteria.s1Title", bodyKey: "guide.walkthroughs.addCriteria.s1Body" },
      { page: "encadrant", titleKey: "guide.walkthroughs.addCriteria.s2Title", bodyKey: "guide.walkthroughs.addCriteria.s2Body" },
    ],
  },
  {
    id: "create-collaborator",
    allowedProfiles: ["ORG", "MNG", "RH"],
    titleKey: "guide.walkthroughs.createCollaborator.title",
    iconName: "UserPlus",
    guideSectionId: "creer-collaborateur",
    steps: [
      { page: "account", reveal: '[data-tour="account-tab-users"]', anchor: '[data-tour="account-tab-users"]', side: "right", titleKey: "guide.walkthroughs.createCollaborator.s1Title", bodyKey: "guide.walkthroughs.createCollaborator.s1Body" },
      { page: "account", reveal: '[data-tour="account-tab-users"]', anchor: '[data-tour="account-add-collaborator"]', side: "bottom", titleKey: "guide.walkthroughs.createCollaborator.s2Title", bodyKey: "guide.walkthroughs.createCollaborator.s2Body" },
      { page: "account", titleKey: "guide.walkthroughs.createCollaborator.s3Title", bodyKey: "guide.walkthroughs.createCollaborator.s3Body" },
    ],
  },
  {
    id: "manage-licences",
    allowedProfiles: ["ORG", "RH", "GES"],
    titleKey: "guide.walkthroughs.manageLicences.title",
    iconName: "KeyRound",
    guideSectionId: "piloter-licences",
    steps: [
      { page: "account", reveal: '[data-tour="account-tab-licences"]', anchor: '[data-tour="account-licences"]', side: "bottom", titleKey: "guide.walkthroughs.manageLicences.s1Title", bodyKey: "guide.walkthroughs.manageLicences.s1Body" },
      { page: "account", titleKey: "guide.walkthroughs.manageLicences.s2Title", bodyKey: "guide.walkthroughs.manageLicences.s2Body" },
    ],
  },
  {
    id: "participate-challenge",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.participateChallenge.title",
    iconName: "Trophy",
    guideSectionId: "participer-challenge",
    steps: [
      { page: "challenge", anchor: '[data-tour="challenges-filters"]', side: "bottom", titleKey: "guide.walkthroughs.participateChallenge.s1Title", bodyKey: "guide.walkthroughs.participateChallenge.s1Body" },
      { page: "challenge", titleKey: "guide.walkthroughs.participateChallenge.s2Title", bodyKey: "guide.walkthroughs.participateChallenge.s2Body" },
    ],
  },
  {
    id: "cockpit",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.cockpit.title",
    iconName: "Compass",
    guideSectionId: "cockpit",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-piloter"]', anchor: '[data-tour="sub-nav-cockpit"]', side: "right", titleKey: "guide.walkthroughs.cockpit.s1Title", bodyKey: "guide.walkthroughs.cockpit.s1Body" },
      // Le périmètre se choisit une fois, en tête de Pilot, pour tous les écrans.
      { page: "encadrant", reveal: '[data-tour="sub-nav-cockpit"]', anchor: '[data-tour="pilot-scope"]', side: "bottom", titleKey: "guide.walkthroughs.cockpit.sScopeTitle", bodyKey: "guide.walkthroughs.cockpit.sScopeBody" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-cockpit"]', anchor: '[data-tour="cockpit-view"]', side: "top", titleKey: "guide.walkthroughs.cockpit.s2Title", bodyKey: "guide.walkthroughs.cockpit.s2Body" },
      // La revue du lundi n'existe que si la semaine a des points : l'étape est optionnelle.
      { page: "encadrant", reveal: '[data-tour="sub-nav-cockpit"]', anchor: '[data-tour="monday-review"]', side: "bottom", titleKey: "guide.walkthroughs.cockpit.sReviewTitle", bodyKey: "guide.walkthroughs.cockpit.sReviewBody" },
      { page: "encadrant", titleKey: "guide.walkthroughs.cockpit.s3Title", bodyKey: "guide.walkthroughs.cockpit.s3Body" },
    ],
  },
  {
    id: "plan",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.plan.title",
    iconName: "CalendarDays",
    guideSectionId: "plan",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-plan"]', side: "right", titleKey: "guide.walkthroughs.plan.s1Title", bodyKey: "guide.walkthroughs.plan.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-plan"]', anchor: '[data-tour="plan-add"]', side: "bottom", titleKey: "guide.walkthroughs.plan.s2Title", bodyKey: "guide.walkthroughs.plan.s2Body" },
      // Onglets à plat (décision 2.5) : l'ancien écran « Actions d'entraînement » est retiré.
      { page: "encadrant", reveal: '[data-tour="sub-nav-plan"]', anchor: '[data-tour="training-actions-help"]', side: "bottom", titleKey: "guide.walkthroughs.plan.sHelpTitle", bodyKey: "guide.walkthroughs.plan.sHelpBody" },
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-campaigns"]', side: "bottom", titleKey: "guide.walkthroughs.plan.sTabsTitle", bodyKey: "guide.walkthroughs.plan.sTabsBody" },
      { page: "encadrant", titleKey: "guide.walkthroughs.plan.s3Title", bodyKey: "guide.walkthroughs.plan.s3Body" },
    ],
  },
  {
    id: "campaigns",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.campaigns.title",
    iconName: "Target",
    guideSectionId: "campagnes",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-campaigns"]', side: "bottom", titleKey: "guide.walkthroughs.campaigns.s1Title", bodyKey: "guide.walkthroughs.campaigns.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-campaigns"]', anchor: '[data-tour="campaigns-intents"]', side: "top", titleKey: "guide.walkthroughs.campaigns.s2Title", bodyKey: "guide.walkthroughs.campaigns.s2Body" },
      // La visite s'arrête à l'ouverture du cadrage : au-delà, le manager
      // remplit un formulaire, et une visite qui commente une saisie gêne.
      { page: "encadrant", observe: true, advanceWhen: '[data-tour="intent-setup"]', titleKey: "guide.walkthroughs.campaigns.s3Title", bodyKey: "guide.walkthroughs.campaigns.s3Body" },
    ],
  },
  {
    id: "create-challenge",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createChallenge.title",
    iconName: "Flag",
    guideSectionId: "creer-challenge",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-challenges"]', side: "bottom", titleKey: "guide.walkthroughs.createChallenge.s1Title", bodyKey: "guide.walkthroughs.createChallenge.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-challenges"]', anchor: '[data-tour="challenges-view"]', side: "top", titleKey: "guide.walkthroughs.createChallenge.sListTitle", bodyKey: "guide.walkthroughs.createChallenge.sListBody" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-challenges"]', anchor: '[data-tour="challenge-create"]', side: "bottom", observe: true, advanceWhen: '[data-tour="challenge-dialog"]', titleKey: "guide.walkthroughs.createChallenge.s2Title", bodyKey: "guide.walkthroughs.createChallenge.s2Body" },
    ],
  },
  {
    id: "reporting",
    // RH retiré : `encadrantRoleFor("RH")` vaut null, donc ni bloc Piloter ni
    // page encadrant — la visite lui était injouable.
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.reporting.title",
    iconName: "BarChart3",
    guideSectionId: "reporting",
    // Deux temps, comme les visites Concevoir : ouvrir le bloc mène au cockpit
    // (premier sous-onglet), pas à Activité. Sans l'étape intermédiaire, la vue
    // n'est jamais montée et le popover flotte au centre.
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-piloter"]', anchor: '[data-tour="sub-nav-activite"]', side: "right", titleKey: "guide.walkthroughs.reporting.s1Title", bodyKey: "guide.walkthroughs.reporting.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-activite"]', anchor: '[data-tour="activity-view"]', side: "top", titleKey: "guide.walkthroughs.reporting.s2Title", bodyKey: "guide.walkthroughs.reporting.s2Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-progression"]', anchor: '[data-tour="progression-view"]', side: "top", titleKey: "guide.walkthroughs.reporting.s3Title", bodyKey: "guide.walkthroughs.reporting.s3Body" },
    ],
  },
  {
    id: "history",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.history.title",
    iconName: "History",
    guideSectionId: "historique",
    steps: [
      { page: "history", anchor: '[data-tour="history-view"]', side: "top", titleKey: "guide.walkthroughs.history.s1Title", bodyKey: "guide.walkthroughs.history.s1Body" },
      { page: "history", titleKey: "guide.walkthroughs.history.s2Title", bodyKey: "guide.walkthroughs.history.s2Body" },
    ],
  },
  {
    id: "certification-paths",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.certificationPaths.title",
    iconName: "Award",
    guideSectionId: "parcours",
    steps: [
      { page: "certification-paths", anchor: '[data-tour="paths-view"]', side: "top", titleKey: "guide.walkthroughs.certificationPaths.s1Title", bodyKey: "guide.walkthroughs.certificationPaths.s1Body" },
      { page: "certification-paths", titleKey: "guide.walkthroughs.certificationPaths.s2Title", bodyKey: "guide.walkthroughs.certificationPaths.s2Body" },
    ],
  },
  // ── Nouveaux sujets (séances, duels, tournois, parcours manager) ──
  {
    id: "seances",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.seances.title",
    iconName: "CalendarDays",
    guideSectionId: "seances",
    steps: [
      { page: "dashboard", anchor: '[data-tour="my-seances"]', side: "bottom", titleKey: "guide.walkthroughs.seances.s1Title", bodyKey: "guide.walkthroughs.seances.s1Body" },
      { titleKey: "guide.walkthroughs.seances.s2Title", bodyKey: "guide.walkthroughs.seances.s2Body" },
    ],
  },
  {
    id: "duels",
    allowedProfiles: ALL_CLIENTS,
    titleKey: "guide.walkthroughs.duels.title",
    iconName: "Swords",
    guideSectionId: "duels",
    steps: [
      { page: "challenge", reveal: '[data-tour="ws-section-duels"]', anchor: '[data-tour="duels-view"]', side: "top", titleKey: "guide.walkthroughs.duels.s1Title", bodyKey: "guide.walkthroughs.duels.s1Body" },
      { titleKey: "guide.walkthroughs.duels.s2Title", bodyKey: "guide.walkthroughs.duels.s2Body" },
    ],
  },
  {
    id: "create-tournament",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createTournament.title",
    iconName: "Medal",
    guideSectionId: "tournois",
    steps: [
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-tournaments"]', side: "bottom", titleKey: "guide.walkthroughs.createTournament.s1Title", bodyKey: "guide.walkthroughs.createTournament.s1Body" },
      { page: "encadrant", reveal: '[data-tour="sub-nav-tournaments"]', anchor: '[data-tour="tournament-create"]', side: "bottom", titleKey: "guide.walkthroughs.createTournament.s2Title", bodyKey: "guide.walkthroughs.createTournament.s2Body" },
    ],
  },
  {
    id: "create-path",
    allowedProfiles: ["ORG", "MNG"],
    titleKey: "guide.walkthroughs.createPath.title",
    iconName: "GraduationCap",
    guideSectionId: "creer-parcours",
    steps: [
      // « Parcours » a quitté Concevoir pour Agir (SUBNAV.animer, EncadrantPage).
      // Révéler Concevoir ne rendait plus jamais `sub-nav-paths` : l'attente
      // expirait et l'étape décrivait un écran invisible.
      { page: "encadrant", reveal: '[data-tour="bloc-nav-animer"]', anchor: '[data-tour="sub-nav-paths"]', side: "bottom", titleKey: "guide.walkthroughs.createPath.s1Title", bodyKey: "guide.walkthroughs.createPath.s1Body" },
      { page: "encadrant", titleKey: "guide.walkthroughs.createPath.s2Title", bodyKey: "guide.walkthroughs.createPath.s2Body" },
    ],
  },
];

// Ordre LOGIQUE des visites dans le menu d'aide (parcours utilisateur), indépendant
// de l'ordre de déclaration : Orientation → S'entraîner (Play) → Concevoir → Animer
// → Piloter → Administrer (Pilot). Le filtrage par profil préserve ensuite cet ordre.
const WALKTHROUGH_ORDER = [
  // Orientation
  "discover-home", "discover-pilot",
  // S'entraîner + progresser + se motiver (opérationnel / Play)
  "studio", "train-simulator", "sessions-live", "seances", "certification-paths",
  "participate-challenge", "duels", "history",
  // Piloter puis Agir (Pilot) — l'ordre dans lequel un manager les découvre :
  // il constate d'abord, il monte ensuite.
  "cockpit", "plan", "campaigns",
  // Concevoir (Pilot)
  "create-offer", "create-persona", "create-context", "create-case", "add-criteria", "create-path",
  // Animer (Pilot)
  "create-challenge", "create-tournament",
  "reporting",
  // Administrer
  "create-collaborator", "manage-licences",
];
const orderIndex = (id: string) => { const i = WALKTHROUGH_ORDER.indexOf(id); return i === -1 ? 999 : i; };

// Espace concerné par chaque visite : "play" (opérationnel), "pilot" (encadrant),
// "both" (transverse, dans les deux menus). Sert à filtrer le menu d'aide selon la
// vue active (Play ↔ Pilot) pour les profils qui ont le switcher.
export type WalkthroughScope = "play" | "pilot" | "both";
const WALKTHROUGH_SCOPE: Record<string, WalkthroughScope> = {
  "discover-home": "play", "studio": "play", "train-simulator": "play", "sessions-live": "play",
  "certification-paths": "play", "participate-challenge": "play", "history": "play",
  "seances": "play", "duels": "play",
  "discover-pilot": "pilot", "create-offer": "pilot", "create-persona": "pilot",
  "create-context": "pilot", "create-case": "pilot", "add-criteria": "pilot",
  "create-path": "pilot", "create-challenge": "pilot", "create-tournament": "pilot",
  "reporting": "pilot", "create-collaborator": "pilot", "manage-licences": "pilot",
  "cockpit": "pilot", "campaigns": "pilot", "plan": "pilot",
};
export function walkthroughScope(id: string): WalkthroughScope { return WALKTHROUGH_SCOPE[id] ?? "both"; }

export function getWalkthroughsForProfile(profileType: ProfileType): Walkthrough[] {
  const internal = isInternalProfile(profileType);
  return WALKTHROUGHS
    .filter((w) => internal || w.allowedProfiles.includes(profileType))
    .sort((a, b) => orderIndex(a.id) - orderIndex(b.id));
}

export function getWalkthrough(id: string): Walkthrough | undefined {
  return WALKTHROUGHS.find((w) => w.id === id);
}

/** Visite guidée reliée à une section du guide écrit (bouton « Visite guidée »). */
/**
 * La visite d'une section du guide, pour l'espace où l'on se trouve.
 *
 * « Prise en main » est commune aux deux espaces et porte donc DEUX visites :
 * l'accueil pour Play, la navigation encadrante pour Pilot. Sans le paramètre
 * `scope`, on renvoyait toujours la première déclarée — un manager en espace
 * Pilot lançait la visite de l'accueil apprenant.
 */
export function getWalkthroughByGuideSection(
  sectionId: string,
  scope?: WalkthroughScope | null,
): Walkthrough | undefined {
  const candidats = WALKTHROUGHS.filter((w) => w.guideSectionId === sectionId);
  if (candidats.length <= 1 || !scope) return candidats[0];
  return candidats.find((w) => walkthroughScope(w.id) === scope) ?? candidats[0];
}
